import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Lead } from '../types/Lead';
import { COLLECTION_NAMES } from '../utils/constants';

export class FirestoreLeadService {
  private static instance: FirestoreLeadService;
  private leadsCache: Lead[] | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): FirestoreLeadService {
    if (!FirestoreLeadService.instance) {
      FirestoreLeadService.instance = new FirestoreLeadService();
    }
    return FirestoreLeadService.instance;
  }

  /**
   * Load all leads from Firestore
   */
  async loadLeads(): Promise<Lead[]> {
    try {
      // Check cache first
      if (this.leadsCache && Date.now() - this.lastFetch < this.CACHE_DURATION) {
        console.log('📦 Returning cached leads');
        return this.leadsCache;
      }

      console.log('🔥 Loading leads from Firestore...');
      const leadsCollection = collection(db, COLLECTION_NAMES.LEADS);
      const snapshot = await getDocs(leadsCollection);

      // Handle empty collection gracefully
      if (snapshot.empty) {
        console.log('📭 No leads found in Firestore collection');
        this.leadsCache = [];
        this.lastFetch = Date.now();
        return [];
      }

      const leads: Lead[] = [];
      snapshot.forEach((doc) => {
        try {
          const data = doc.data();
          // Remove Firestore metadata fields
          const { uploadedAt, batchNumber, filename, ...leadData } = data;

          // Only process documents that have the minimum required structure
          if (!data.metadata?.companyName || !data.processed?.averageScore) {
            console.warn(`⚠️ Skipping lead document ${doc.id} - missing required fields`);
            return;
          }

          // Ensure the lead has the required structure
          const lead: Lead = {
            id: doc.id, // Use Firestore document ID
            ...leadData as Lead,
            // Store original filename if available
            ...(filename && { originalFilename: filename })
          };

          // Only add leads that have all required fields
          if (lead.metadata && lead.processed && lead.overallScore && lead.metrics && lead.topContacts) {
            leads.push(lead);
          } else {
            console.warn(`⚠️ Skipping lead document ${doc.id} - incomplete data structure`);
          }
        } catch (docError) {
          console.warn(`⚠️ Error processing lead document ${doc.id}:`, docError);
          // Skip malformed documents instead of crashing
        }
      });

      // Sort by average score (descending) with null safety
      leads.sort((a, b) => {
        const scoreA = a.processed?.averageScore ?? 0;
        const scoreB = b.processed?.averageScore ?? 0;
        return scoreB - scoreA;
      });

      // Update cache
      this.leadsCache = leads;
      this.lastFetch = Date.now();

      console.log(`✅ Loaded ${leads.length} leads from Firestore`);
      return leads;
    } catch (error) {
      console.error('❌ Error loading leads from Firestore:', error);
      // Return empty array instead of throwing error to prevent UI crashes
      this.leadsCache = [];
      this.lastFetch = Date.now();
      return [];
    }
  }



  /**
   * Get a single lead by ID
   */
  async getLeadById(id: string): Promise<Lead | null> {
    try {
      console.log(`🔍 Loading lead ${id} from Firestore...`);
      const docRef = doc(db, COLLECTION_NAMES.LEADS, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const { uploadedAt, batchNumber, filename, ...leadData } = data;

        // Only process documents that have the minimum required structure
        if (!data.metadata?.companyName || !data.processed?.averageScore) {
          console.warn(`⚠️ Skipping lead document ${docSnap.id} - missing required fields`);
          return null;
        }

        const lead: Lead = {
          id: docSnap.id,
          ...leadData as Lead,
          ...(filename && { originalFilename: filename })
        };

        // Only return leads that have all required fields
        if (lead.metadata && lead.processed && lead.overallScore && lead.metrics && lead.topContacts) {
          console.log(`✅ Found lead: ${lead.metadata.companyName}`);
          return lead;
        } else {
          console.warn(`⚠️ Lead ${docSnap.id} has incomplete data structure`);
          return null;
        }
      } else {
        console.log(`❌ Lead ${id} not found`);
        return null;
      }
    } catch (error) {
      console.error(`❌ Error loading lead ${id}:`, error);
      throw new Error(`Failed to load lead ${id}`);
    }
  }

  /**
   * Search leads by company name
   */
  async searchLeadsByCompany(searchTerm: string): Promise<Lead[]> {
    try {
      console.log(`🔍 Searching leads by company: "${searchTerm}"`);

      // For now, load all leads and filter client-side
      // In production, you'd want to use Firestore's full-text search or Algolia
      const allLeads = await this.loadLeads();

      if (allLeads.length === 0) {
        console.log('📭 No leads available for search');
        return [];
      }

      const filteredLeads = allLeads.filter(lead =>
        lead.metadata.companyName.toLowerCase().includes(searchTerm.toLowerCase())
      );

      console.log(`✅ Found ${filteredLeads.length} leads matching "${searchTerm}"`);
      return filteredLeads;
    } catch (error) {
      console.error('❌ Error searching leads:', error);
      return []; // Return empty array instead of throwing error
    }
  }

  /**
   * Get leads by score range
   */
  async getLeadsByScoreRange(minScore: number, maxScore: number): Promise<Lead[]> {
    try {
      console.log(`🔍 Loading leads with score between ${minScore} and ${maxScore}...`);

      const allLeads = await this.loadLeads();

      if (allLeads.length === 0) {
        console.log('📭 No leads available for score filtering');
        return [];
      }

      const filteredLeads = allLeads.filter(lead => {
        const score = lead.processed?.averageScore ?? 0;
        return score >= minScore && score <= maxScore;
      });

      console.log(`✅ Found ${filteredLeads.length} leads in score range`);
      return filteredLeads;
    } catch (error) {
      console.error('❌ Error loading leads by score range:', error);
      return []; // Return empty array instead of throwing error
    }
  }

  /**
   * Clear cache to force refresh
   */
  clearCache(): void {
    this.leadsCache = null;
    this.lastFetch = 0;
    console.log('🗑️ Lead cache cleared');
  }

  /**
   * Get cache status
   */
  getCacheStatus(): {
    hasCachedData: boolean;
    cacheAge: number;
    leadCount: number;
  } {
    return {
      hasCachedData: this.leadsCache !== null,
      cacheAge: Date.now() - this.lastFetch,
      leadCount: this.leadsCache?.length || 0
    };
  }

  // Note: Members are now managed in a separate collection
  // No need for member-specific cache methods

  /**
   * Get a lead from cache by ID
   */
  getLeadFromCache(leadId: string): Lead | null {
    if (!this.leadsCache) return null;
    return this.leadsCache.find(lead => lead.id === leadId) || null;
  }

  /**
   * Update a single lead in Firestore
   */
  async updateLead(leadId: string, updates: Partial<Lead>): Promise<void> {
    try {
      const leadRef = doc(db, COLLECTION_NAMES.LEADS, leadId);
      await updateDoc(leadRef, updates);

      // Update cache if it exists
      if (this.leadsCache) {
        const leadIndex = this.leadsCache.findIndex(lead => lead.id === leadId);
        if (leadIndex !== -1) {
          this.leadsCache[leadIndex] = {
            ...this.leadsCache[leadIndex],
            ...updates
          };
        }
      }

      console.log(`✅ Updated lead ${leadId} in Firestore`);
    } catch (error) {
      console.error(`❌ Error updating lead ${leadId}:`, error);
      throw new Error(`Failed to update lead ${leadId}`);
    }
  }
}

// Export singleton instance
export const firestoreLeadService = FirestoreLeadService.getInstance();